import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type Dispatch,
  type SetStateAction
} from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type ReactEChartsCoreType from 'echarts-for-react/lib/core';
import type { EChartsOption } from 'echarts';
import { TreeChart } from 'echarts/charts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import {
  Button,
  Card,
  Form,
  OverlayTrigger,
  Popover
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useOutletContext, useParams } from 'react-router-dom';
import { useGetDiagramMutation } from '@/app/Crawl/api/diagramApi';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import { useAppContext } from '@/Main';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';
import TreeSkeleton from '@/app/common/components/skeleton/TreeSkeleton';
import useDomain from '@/hooks/useDomain';
import { useTooltipHide } from '@/hooks/useTooltipHide';
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  TreeChart,
  CanvasRenderer
]);
// نمودار درخت مرکزی
type BreadcrumbItem = {
  text: string;
  href: string;
};

type CrawlOutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type TreeNode = {
  name?: string;
  status_code?: number | string;
  status?: string;
  depth?: number | string;
  ps_title?: string;
  page_source?: string;
  referer?: string;
  children?: TreeNode[];
  [key: string]: unknown;
};

type TreeTooltipParams = {
  name?: string;
  data: TreeNode;
};

const TreeRadial = () => {
  const { slug } = useParams();
  const { domain_slug, domain } = useDomain();
  const [, setBreadcrumbData] =
    useOutletContext<CrawlOutletContext>();
  useEffect(() => {
    setBreadcrumbData([
      {
        text: 'گراف پیوند',
        href: `/crawl/linkgraph/${slug}/${domain_slug}/${domain}`
      },
      { text: 'درخت مرکزی', href: '#' }
    ]);
  }, []);
  const chartRef = useRef<ReactEChartsCoreType | null>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [maxZoomLevel] = useState(20);
  const [minZoomLevel] = useState(0.001);
  // ریست کردن زوم نمودار
  const handleZoomReset = () => {
    setZoomLevel(1);
    chartRef.current?.getEchartsInstance().dispatchAction({
      type: 'restore'
    });
  };
  // افزایش زوم نمودار
  const handleZoomIn = () => {
    if (zoomLevel < maxZoomLevel) {
      setZoomLevel(zoomLevel + 0.3);
    }
    chartRef.current?.getEchartsInstance().setOption({
      series: {
        zoom: zoomLevel + 0.3
      }
    });
  };
  // کاهش زوم نمودار
  const handleZoomOut = () => {
    if (zoomLevel > minZoomLevel) {
      setZoomLevel(zoomLevel - 0.3);
    }
    chartRef.current?.getEchartsInstance().setOption({
      series: {
        zoom: zoomLevel - 0.3
      }
    });
  };
  // دریافت نمودار
  useGetUserInfoQuery();

  const [getDiagram, { isLoading, data: rawData }] =
    useGetDiagramMutation();
  const data = rawData as { data?: TreeNode } | undefined;
  const job_page = slug === 'last' ? domain_slug : slug;

  useEffect(() => {
    getDiagram({ job_page: job_page, domain_slug, limit: 3000 });
  }, []);

  const getOption = (
    data: TreeNode | undefined,
    fontSize: number | undefined
  ): EChartsOption => ({
    textStyle: {
      fontFamily: 'dana'
    },
    tooltip: {
      triggerOn: isMdUp ? 'mousemove' : 'click',
      trigger: 'item',
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100'), fontSize: fontSize },
      borderWidth: 1,
      formatter: (params: TreeTooltipParams) => {
        return `<div>
     <div style="direction:rtl;" class="d-flex gap-1 justify-content-start" >
        <div class="d-flex flex-column flex-md-row gap-0 align-items-start align-items-md-center justify-content-between w-100 gap-1">     
        <span class="fw-bolder">
        مسیر: 
        </span>
        <span style="direction:ltr;"> ${params.name} </span>
        </div>
        </div>
        
        <span class="fw-bolder">
        کدوضعیت: 
        </span>
        <span>${params.data.status_code}</span><br/>
        <span class="fw-bolder"> وضعیت: </span> <span> ${params.data.status}</span><br/>
        <span class="fw-bolder"> عمق: </span> <span> ${params.data.depth}</span><br/>
        <div class="d-flex flex-column flex-md-row gap-0 align-items-start align-items-md-center justify-content-between w-100 gap-1">     
        <span class="fw-bolder"> عنوان نشانی: </span>
        <span>${params.data.ps_title}</span>
        </div>
        </div>
        <div style="direction:rtl;" class="d-flex gap-1 justify-content-start" >
        <div class="d-flex flex-column flex-md-row gap-0 align-items-start align-items-md-center justify-content-between w-100 gap-1">     
        <span class="fw-bolder">نشانی کامل:</span>
        <span style="direction:ltr;">
          ${params.data.page_source}
          </span>
        </div>
        </div>
        <div class="d-flex flex-column flex-md-row gap-0 align-items-start align-items-md-center justify-content-between w-100 gap-1">     
        <div style="direction:rtl;" class="d-flex gap-1 justify-content-start" >
        <span class="fw-bolder">صفحه ارجاع:</span>
        <span style="direction:ltr;">
          ${params.data.referer}
        </span>
        </div>
        </div>`;
      },
      Duration: 0,
      axisPointer: {
        type: 'none'
      }
    },
    series: [
      {
        roam: true,
        scaleLimit: {
          min: minZoomLevel,
          max: maxZoomLevel
        },
        type: 'tree',
        data: [data],
        top: '18%',
        bottom: '14%',
        layout: 'radial',
        itemStyle: {
          color: getColor('primary'),
          borderRadius: [0, 3, 3, 0]
        },
        right: '20%',
        lineStyle: { color: getColor('primary') },
        label: {
          position: 'left',
          verticalAlign: 'middle',
          align: 'right',
          color: getColor('gray-1100'),
          border: 0,
          fontSize: fontSize
        },
        showSymbol: true,
        symbolSize: 10,

        leaves: {
          label: {
            position: 'right',
            verticalAlign: 'middle',
            align: 'left'
          },
          itemStyle: {
            color: getColor('primary'),
            borderRadius: [0, 3, 3, 0]
          }
        },
        emphasis: {
          focus: 'descendant'
        },
        expandAndCollapse: true,
        animationDuration: 550,
        animationDurationUpdate: 750
      }
    ]
  } as unknown as EChartsOption);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const {
    config: { isDark }
  } = useAppContext();
  const [fontSize, setFontSize] = useState<number | undefined>(undefined);
  return (
    <>
      {!isLoading && data ? (
        <Card className="my-2" ref={cardRef}>
          <FalconCardHeader
            title={
              <div>
                درخت مرکزی
                <HelpTooltip html={crawlTooltip.crawlerLinkGraph.circle} />
              </div>
            }
            titleTag="h6"
            className="py-2"
            light
            endEl={<CardChartDropDown cardRef={cardRef} chartRef={chartRef} />}
          />
          <Card.Body className="py-2 my-2 position-relative">
            <ReactEChartsCore
              ref={chartRef}
              echarts={echarts}
              option={getOption(data.data, fontSize)}
              style={{ height: '500px' }}
            />
            {/* دکمه های نمودار */}
            <div className="position-absolute d-flex flex-column top-0 mt-3">
              <div className="rounded-3 bg-body-tertiary border-400 w-100 border">
                <Button
                  variant="link"
                  size="sm"
                  className="bg-100 d-flex align-items-center justify-content-center rounded-bottom-0 p-2 text-700"
                  onClick={handleZoomIn}
                >
                  <FontAwesomeIcon icon="plus" className="fs--1" />
                </Button>
                <hr className="m-0 text-300" />
                <Button
                  variant="link"
                  size="sm"
                  className="bg-100 d-flex align-items-center justify-content-center rounded-top-0  p-2 text-700"
                  onClick={handleZoomReset}
                >
                  <FontAwesomeIcon icon="undo-alt" className="fs--1" />
                </Button>
                <hr className="m-0 text-300" />
                <Button
                  variant="link"
                  size="sm"
                  className="bg-100 d-flex align-items-center justify-content-center rounded-top-0  p-2 text-700"
                  onClick={handleZoomOut}
                >
                  <FontAwesomeIcon icon="minus" className="fs--1" />
                </Button>
              </div>
              <div className="bg-body-tertiary mt-3">
                <div className="bg-body-tertiary mt-1 ">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      // تمام صفحه کردن نمودار
                      if (!document.fullscreenElement) {
                        cardRef.current?.requestFullscreen();
                      } else {
                        document.exitFullscreen();
                      }
                    }}
                    className="bg-100 d-flex align-items-center justify-content-center rounded-3 border border-400 w-100 p-2 text-700"
                  >
                    <FontAwesomeIcon
                      icon={
                        document.fullscreenElement
                          ? 'compress-arrows-alt'
                          : 'expand-arrows-alt'
                      }
                      className="fs--1"
                    />
                  </Button>
                </div>
              </div>
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom"
                overlay={
                  <Popover id="popover-basic" style={{ width: '350px' }}>
                    <Popover.Header
                      as="h3"
                      className="d-flex justify-content-between align-items-center"
                    >
                      تغییر اندازه فونت
                      <span className="fs--1" style={{ direction: 'ltr' }}>
                        {fontSize} px
                      </span>
                    </Popover.Header>
                    <Popover.Body className="p-0">
                      <div
                        className="p-2 d-flex align-items-center"
                        style={{ direction: 'ltr' }}
                      >
                        <Form.Range
                          {...({
                            max: 50,
                            min: 0,
                            value: fontSize,
                            variant: 'primary',
                            onChange: (e: ChangeEvent<HTMLInputElement>) =>
                              setFontSize(Number(e.target.value))
                          } as unknown as ComponentProps<typeof Form.Range>)}
                        />
                      </div>
                    </Popover.Body>
                  </Popover>
                }
              >
                <div className="bg-body-tertiary mt-1 ">
                  <Button
                    variant="link"
                    size="sm"
                    className="bg-100 d-flex align-items-center justify-content-center rounded-3 border border-400 w-100  p-2 text-700"
                  >
                    <FontAwesomeIcon icon="text-height" className="fs--1" />
                  </Button>
                </div>
              </OverlayTrigger>
              <div className="bg-body-tertiary mt-1 ">
                <Button
                  onClick={() => {
                    // ذخیره عکس
                    const chart = chartRef.current;
                    if (!chart) return;
                    const dataURL = chart
                      .getEchartsInstance()
                      .getDataURL({
                        type: 'png',
                        pixelRatio: 1,
                        backgroundColor: isDark ? '#121e2d' : '#fff'
                      });
                    const link = document.createElement('a');
                    link.href = dataURL;
                    link.setAttribute('download', 'chart-from-Mizfa-Tools.png');
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  variant="link"
                  size="sm"
                  className="bg-100 d-flex align-items-center justify-content-center rounded-3 border border-400 w-100  p-2 text-700"
                >
                  <FontAwesomeIcon icon="save" className="fs--1" />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <TreeSkeleton title="درخت مرکزی" height={'800px'} />
      )}
    </>
  );
};
export default TreeRadial;
